import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { intlLocale } from '../i18n.js';
import { useLocale, usePlanState } from './site.js';
import { SeverityBadge, type Severity } from './ui.js';

export type Improvement = Schemas['Improvement'];

/** A problem first seen in a period: only its title and severity, never its details (T-101). */
export interface NewProblem {
  readonly ruleCode: string;
  readonly title: string;
  readonly severity: Severity;
  readonly firstSeenAt: string;
}

/** What got better on a site in the last `days` days (T-36), newest first. */
export function useImprovements(siteId: string, days: number, enabled = true) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['site', siteId, 'improvements', days, locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/improvements', {
          params: { path: { siteId }, query: { days, locale } },
        }),
      ),
    enabled,
  });
}

/**
 * How many problems got fixed lately, beside the open ones (the side panel, Probleme): in the
 * last 30 days, or in the plan's whole history when it is shorter, as the API would cut the
 * period silently (SP-A2.69). Asked once the plan is known; 7 days when it did not load, which
 * every plan has (as on Verbesserungen).
 */
export function useRecentlyFixed(siteId: string) {
  const { plan, settled } = usePlanState();
  const history = plan?.limits.historyDays;
  const days = history === undefined ? 7 : Math.min(30, history);
  const improvements = useImprovements(siteId, days, settled);
  // Nothing before the plan: the dashboard's 7 days could stand in the cache meanwhile.
  const fixed = settled
    ? (improvements.data?.items.filter((i) => i.kind === 'fixed').length ?? null)
    : null;
  return { days, fixed };
}

/**
 * The good news with before → after and what they bring, then the problems that are new
 * (site-dashboard.html and site-changes.html .changes): the same list on the dashboard's week
 * and on the page of all improvements.
 */
export function ChangeList({
  siteId,
  items,
  news,
  timeZone,
}: {
  siteId: string;
  items: readonly Improvement[];
  news: readonly NewProblem[];
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const day = (iso: string) =>
    new Intl.DateTimeFormat(intlLocale(i18n.language), {
      day: '2-digit',
      month: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).format(new Date(iso));
  return (
    <ul className="changes">
      {items.map((item) => (
        <li key={`${item.kind}-${item.at}-${item.title}`}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M5 12l4 4L19 6" />
          </svg>
          <div>
            <b>{item.title}</b>
            {item.from && item.to ? (
              <span className="arr">
                {item.from}
                <span aria-hidden="true"> → </span>
                <span className="sr-only"> {t('changes.to')} </span>
                {item.to}
              </span>
            ) : null}
            <p>
              {t('changes.gain')} {item.gain}
            </p>
          </div>
          <time dateTime={item.at}>{day(item.at)}</time>
        </li>
      ))}
      {news.map((n) => (
        <li key={n.ruleCode} className="new">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M12 3l10 18H2z" />
            <path d="M12 10v4M12 17h.01" />
          </svg>
          <div>
            <b>
              {t('changes.newLabel')}{' '}
              <Link to={`/sites/${siteId}/findings?open=${encodeURIComponent(n.ruleCode)}`}>
                {n.title}
              </Link>
            </b>
            <SeverityBadge severity={n.severity} />
          </div>
          <time dateTime={n.firstSeenAt}>{day(n.firstSeenAt)}</time>
        </li>
      ))}
    </ul>
  );
}
